import { useState } from "react";
import type { Campaign } from "../../../mocks/campaigns";

const DISTRICTS = ["Osmangazi", "Yıldırım", "Nilüfer", "Mudanya", "Karacabey", "Gemlik"];
const VENUE_CATEGORIES = ["Tarihi Yerler", "Lezzet Durakları", "Eğlence", "Konaklama", "Doğa ve Rotalar", "Gezilecek Yerler"];

interface CampaignFormModalProps {
  mode: "add" | "edit";
  initial?: Partial<Campaign>;
  onSave: (data: Partial<Campaign>) => void;
  onClose: () => void;
}

export default function CampaignFormModal({ mode, initial = {}, onSave, onClose }: CampaignFormModalProps) {
  const [form, setForm] = useState({
    title: initial.title ?? "",
    venueName: initial.venueName ?? "",
    venueCategory: initial.venueCategory ?? "Lezzet Durakları",
    discountPct: initial.discountPct ?? 10,
    description: initial.description ?? "",
    expiresDate: initial.expiresDate ?? "",
    couponCode: initial.couponCode ?? "",
    isSponsored: initial.isSponsored ?? false,
    district: initial.district ?? "Osmangazi",
    image: initial.image ?? "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.venueName.trim()) return;
    onSave({ ...form, discountPct: Number(form.discountPct) });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-stone-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="font-bold text-stone-800">
            {mode === "add" ? "Yeni Kampanya Ekle" : "Kampanyayı Düzenle"}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Kampanya Başlığı *</label>
            <input required type="text" value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Örn: Tüm Aktivitelerde %30 İndirim"
              className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Mekan Adı *</label>
              <input required type="text" value={form.venueName}
                onChange={(e) => setForm((f) => ({ ...f, venueName: e.target.value }))}
                placeholder="Mekan adı"
                className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Mekan Kategorisi</label>
              <select value={form.venueCategory}
                onChange={(e) => setForm((f) => ({ ...f, venueCategory: e.target.value }))}
                className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none cursor-pointer text-stone-700 focus:border-emerald-400">
                {VENUE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">İndirim Oranı (%)</label>
              <input type="number" min="1" max="100" value={form.discountPct}
                onChange={(e) => setForm((f) => ({ ...f, discountPct: Number(e.target.value) }))}
                className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Son Geçerlilik Tarihi</label>
              <input type="text" value={form.expiresDate}
                onChange={(e) => setForm((f) => ({ ...f, expiresDate: e.target.value }))}
                placeholder="15 Mayıs 2026"
                className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Kupon Kodu</label>
              <input type="text" value={form.couponCode}
                onChange={(e) => setForm((f) => ({ ...f, couponCode: e.target.value.toUpperCase() }))}
                placeholder="INDIRIM20"
                className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors font-mono tracking-wider" />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">İlçe</label>
              <select value={form.district}
                onChange={(e) => setForm((f) => ({ ...f, district: e.target.value }))}
                className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none cursor-pointer text-stone-700 focus:border-emerald-400">
                {DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Açıklama</label>
            <textarea rows={3} value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Kampanya detayı..."
              maxLength={500}
              className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors resize-none" />
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Görsel URL</label>
            <input type="url" value={form.image}
              onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))}
              placeholder="https://..."
              className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors" />
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <div onClick={() => setForm((f) => ({ ...f, isSponsored: !f.isSponsored }))}
              className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${form.isSponsored ? "bg-amber-500" : "bg-stone-200"}`}>
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${form.isSponsored ? "translate-x-5" : "translate-x-0.5"}`} />
            </div>
            <span className="text-sm text-stone-700">Sponsorlu Kampanya</span>
          </label>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-stone-200 rounded-xl text-sm text-stone-600 hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap">
              İptal
            </button>
            <button type="submit"
              className="flex-1 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
              {mode === "add" ? "Kampanya Oluştur" : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
